import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Briefcase, CheckCircle, ChevronLeft, ChevronRight, DollarSign, ExternalLink, Sparkles, XCircle, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { trackCampaignCreated } from '../../../utils/analytics';
import { API, getHeaders, useBrand } from '../portalShared';

export default function CampaignsList() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [budgetModal, setBudgetModal] = useState(null);
  const [budgetAmount, setBudgetAmount] = useState('500');
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showRecommender, setShowRecommender] = useState(false);
  const [recoForm, setRecoForm] = useState({ goal: '', funnel_stage: 'awareness', budget_band: '$5-25K' });
  const [recoResult, setRecoResult] = useState(null);
  const [recoBusy, setRecoBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', title: '', company: '', linkedin_url: '', message: '' });
  const [inviting, setInviting] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [uploadPrivate, setUploadPrivate] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [newCampaign, setNewCampaign] = useState({
    title: '', description: '', industry: '', platforms: ['LinkedIn'], compensation_type: 'flat',
    flat_fee: '', cpm_rate: '', floor_guarantee: '', performance_bonus_amount: '', performance_bonus_threshold: '',
    exec_tier_required: 'Open', min_followers: 0, spots_total: 10, deadline: '',
    talking_points: [''], guardrails: [''], example_post: '', visibility: 'public', posts_per_exec: 1,
    offering_type: 'endorsement_post',
    survey_external_url: '',
    survey_questions: [],
    // Phase 2: sessions duration + consulting project
    session_duration_min: 30,
    project_scope: '',
    project_milestones: [],
    // Phase 4: AI training Q&A
    ai_training_questions: [],
    ai_training_pay_per_question: '',
  });
  const [offeringTypes, setOfferingTypes] = useState([]);
  const [tierMeta, setTierMeta] = useState({});
  const brand = useBrand();

  const fetchCampaigns = () => { axios.get(`${API}/portal/campaigns`, { headers: getHeaders() }).then(r => setCampaigns(r.data)).catch(() => {}); };
  useEffect(() => { fetchCampaigns(); }, []);
  useEffect(() => {
    axios.get(`${API}/offerings/types`).then(r => {
      setOfferingTypes(r.data.types || []);
      setTierMeta(r.data.tiers || {});
    }).catch(() => {});
  }, []);

  const handleCreateCampaign = async () => {
    if (!newCampaign.title.trim()) { toast.error('Campaign title is required'); return; }
    if (newCampaign.offering_type === 'survey') {
      const hasUrl = !!newCampaign.survey_external_url.trim();
      const hasQs = (newCampaign.survey_questions || []).length > 0;
      if (!hasUrl && !hasQs) { toast.error('Add at least one survey question or paste an external URL'); return; }
      if (hasUrl && hasQs) { toast.error('Choose native questions OR external URL — not both'); return; }
    }
    if (newCampaign.offering_type === 'consulting_project') {
      if (!newCampaign.project_scope.trim()) { toast.error('Project scope is required'); return; }
      if (!(newCampaign.project_milestones || []).length) { toast.error('Add at least one milestone'); return; }
      for (const m of newCampaign.project_milestones) {
        if (!(m.title || '').trim()) { toast.error('Every milestone needs a title'); return; }
        if (!m.amount || parseFloat(m.amount) <= 0) { toast.error('Every milestone needs an amount > 0'); return; }
      }
    }
    if (newCampaign.offering_type === 'ai_training') {
      if (!(newCampaign.ai_training_questions || []).length) { toast.error('Add at least one training question'); return; }
      for (const q of newCampaign.ai_training_questions) {
        if (!(q.prompt || '').trim()) { toast.error('Every question needs a prompt'); return; }
      }
    }
    setCreating(true);
    try {
      const payload = {
        ...newCampaign,
        flat_fee: newCampaign.flat_fee ? parseFloat(newCampaign.flat_fee) : null,
        cpm_rate: newCampaign.cpm_rate ? parseFloat(newCampaign.cpm_rate) : null,
        floor_guarantee: newCampaign.floor_guarantee ? parseFloat(newCampaign.floor_guarantee) : null,
        performance_bonus_amount: newCampaign.performance_bonus_amount ? parseFloat(newCampaign.performance_bonus_amount) : null,
        performance_bonus_threshold: newCampaign.performance_bonus_threshold ? parseInt(newCampaign.performance_bonus_threshold) : null,
        talking_points: newCampaign.talking_points.filter(t => t.trim()),
        guardrails: newCampaign.guardrails.filter(g => g.trim()),
      };
      delete payload.survey_external_url;
      delete payload.survey_questions;
      delete payload.project_scope;
      delete payload.project_milestones;
      delete payload.ai_training_questions;
      delete payload.ai_training_pay_per_question;
      // For consulting projects, flat_fee = sum of milestones
      if (newCampaign.offering_type === 'consulting_project') {
        payload.flat_fee = newCampaign.project_milestones.reduce((s, m) => s + parseFloat(m.amount || 0), 0);
      }
      const { data: created } = await axios.post(`${API}/portal/campaigns/create`, payload, { headers: getHeaders() });
      // If survey, persist survey definition
      if (newCampaign.offering_type === 'survey') {
        const surveyBody = newCampaign.survey_external_url.trim()
          ? { campaign_id: created.id, external_url: newCampaign.survey_external_url.trim() }
          : { campaign_id: created.id, questions: newCampaign.survey_questions };
        await axios.post(`${API}/portal/surveys`, surveyBody, { headers: getHeaders() });
      }
      // If consulting project, persist project scope + milestones
      if (newCampaign.offering_type === 'consulting_project') {
        await axios.post(`${API}/portal/projects`, {
          campaign_id: created.id,
          scope: newCampaign.project_scope.trim(),
          milestones: newCampaign.project_milestones.map(m => ({
            title: m.title, description: m.description || '', amount: parseFloat(m.amount), due_date: m.due_date || null,
          })),
        }, { headers: getHeaders() });
      }
      // If AI training, persist question set
      if (newCampaign.offering_type === 'ai_training') {
        await axios.post(`${API}/portal/ai-training/${created.id}/questions`, {
          questions: newCampaign.ai_training_questions,
          pay_per_question: parseFloat(newCampaign.ai_training_pay_per_question || 0),
        }, { headers: getHeaders() });
      }
      setShowCreate(false);
      setNewCampaign({ title: '', description: '', industry: '', platforms: ['LinkedIn'], compensation_type: 'flat', flat_fee: '', cpm_rate: '', floor_guarantee: '', performance_bonus_amount: '', performance_bonus_threshold: '', exec_tier_required: 'Open', min_followers: 0, spots_total: 10, deadline: '', talking_points: [''], guardrails: [''], example_post: '', visibility: 'public', posts_per_exec: 1, offering_type: 'endorsement_post', survey_external_url: '', survey_questions: [], session_duration_min: 30, project_scope: '', project_milestones: [], ai_training_questions: [], ai_training_pay_per_question: '' });
      fetchCampaigns();
      // GA4 + Meta Pixel conversion — use total budget (spots × flat_fee) when available, else flat_fee, else 0.
      const perUnit = parseFloat(payload.flat_fee || 0);
      const spots = parseInt(payload.spots_total || 0, 10) || 1;
      const totalBudget = perUnit > 0 ? perUnit * spots : 0;
      trackCampaignCreated({
        campaign_id: created?.id,
        offering_type: payload.offering_type,
        industry: payload.industry,
        value: totalBudget,
        currency: 'USD',
        spots_total: spots,
      });
      toast.success('Campaign created!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create campaign'); }
    finally { setCreating(false); }
  };

  const handleInviteByEmail = async () => {
    if (!inviteForm.email.trim()) { toast.error('Email required'); return; }
    setInviting(true);
    try {
      await axios.post(`${API}/portal/campaigns/${selectedCampaign.id}/invite-by-email`, inviteForm, { headers: getHeaders() });
      setShowInvite(false);
      setInviteForm({ name: '', email: '', title: '', company: '', linkedin_url: '', message: '' });
      openTracking(selectedCampaign);
      toast.success('Invitation sent!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to invite'); }
    finally { setInviting(false); }
  };

  const handleUploadList = async () => {
    const lines = csvText.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) { toast.error('Paste at least one contact'); return; }
    const contacts = lines.map(line => {
      const parts = line.split(',').map(s => s.trim());
      return { name: parts[0] || '', email: parts[1] || '', title: parts[2] || '', company: parts[3] || '', linkedin_url: parts[4] || '' };
    }).filter(c => c.email);
    if (contacts.length === 0) { toast.error('No valid emails found. Format: Name, Email, Title, Company, LinkedIn URL'); return; }
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/portal/campaigns/${selectedCampaign.id}/upload-list`, {
        contacts, private_list: uploadPrivate, message: uploadMessage
      }, { headers: getHeaders() });
      setUploadResult(data);
      openTracking(selectedCampaign);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleAddBudget = async () => {
    if (!budgetModal || !budgetAmount) return;
    setBudgetLoading(true);
    try {
      const { data } = await axios.post(`${API}/portal/campaigns/${budgetModal.id}/add-budget`, {
        amount: parseFloat(budgetAmount),
        origin_url: window.location.origin
      }, { headers: getHeaders() });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBudgetLoading(false); }
  };

  const openTracking = async (campaign) => {
    setSelectedCampaign(campaign);
    setTrackingLoading(true);
    try {
      const { data } = await axios.get(`${API}/portal/campaigns/${campaign.id}/tracking`, { headers: getHeaders() });
      setTracking(data);
    } catch {} finally { setTrackingLoading(false); }
  };

  const markGoodFit = async (participantId, value) => {
    try {
      await axios.post(`${API}/portal/campaigns/${selectedCampaign.id}/good-fit/${participantId}`, { good_fit: value }, { headers: getHeaders() });
      openTracking(selectedCampaign);
    } catch {}
  };

  const inviteToNext = async (participantId) => {
    const otherCampaigns = campaigns.filter(c => c.id !== selectedCampaign?.id && c.status === 'active');
    if (otherCampaigns.length === 0) { toast.error('No other active campaigns to invite to.'); return; }
    const nextId = otherCampaigns[0].id;
    try {
      await axios.post(`${API}/portal/campaigns/${selectedCampaign.id}/invite-next/${participantId}`, { next_campaign_id: nextId }, { headers: getHeaders() });
      toast.success('Executive invited to next campaign!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const confirmSchedule = async (participantId) => {
    try {
      await axios.post(`${API}/portal/workspace/${participantId}/confirm-schedule`, { action: 'confirm' }, { headers: getHeaders() });
      openTracking(selectedCampaign);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Campaign Tracking Detail View
  if (selectedCampaign && tracking) {
    const s = tracking.summary;
    return (
      <div className="space-y-6" data-testid="campaign-tracking">
        <button onClick={() => { setSelectedCampaign(null); setTracking(null); }} className="flex items-center gap-2 text-[#9CA3AF] hover:text-white text-sm transition-colors">
          <ChevronLeft className="w-4 h-4" /> Back to Campaigns
        </button>

        <div className="bg-[#111827] border border-white/10 p-4 sm:p-6 border-l-[3px] border-l-[#FF4343]">
          <div className="flex items-center gap-3 mb-2">
            <h2 className="font-display text-xl font-bold">{tracking.campaign?.title}</h2>
            <span className={`text-[10px] font-mono uppercase px-2 py-0.5 ${tracking.campaign?.visibility === 'private' ? 'bg-white/5 text-[#9CA3AF] border border-white/10' : 'bg-[#10B981]/10 text-[#10B981]'}`}>
              {tracking.campaign?.visibility === 'private' ? 'Private' : 'Public'}
            </span>
            <span className="text-[10px] font-mono text-[#6B7280]">{tracking.campaign?.service_type === 'managed' ? 'Managed (20% fee)' : 'Self-Service (15% fee)'}</span>
          </div>
          <p className="text-xs text-[#6B7280]">{tracking.campaign?.industry} &middot; {tracking.campaign?.platforms?.join(', ')}</p>
          <div className="flex gap-2 mt-3">
            <button onClick={() => setShowInvite(true)} data-testid="invite-exec-btn"
              className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-all">
              + Invite Executive by Email
            </button>
            <button onClick={() => { setShowUpload(true); setUploadResult(null); setCsvText(''); }} data-testid="upload-list-btn"
              className="text-xs px-4 py-2 border border-white/20 text-white hover:bg-white/5 transition-all">
              Upload Contact List
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-[#111827] border border-white/10 p-3 sm:p-4 min-w-0">
            <p className="text-[10px] text-[#6B7280] uppercase">Participants</p>
            <p className="font-mono text-xl sm:text-2xl text-white font-bold tabular-nums leading-tight truncate">{s.total_participants}</p>
          </div>
          <div className="bg-[#111827] border border-white/10 p-3 sm:p-4 min-w-0">
            <p className="text-[10px] text-[#6B7280] uppercase">Impressions</p>
            <p className="font-mono text-xl sm:text-2xl text-white font-bold tabular-nums leading-tight truncate">{(s.total_impressions / 1000).toFixed(0)}K</p>
          </div>
          <div className="bg-[#111827] border border-white/10 p-3 sm:p-4 min-w-0">
            <p className="text-[10px] text-[#6B7280] uppercase">Exec Payouts</p>
            <p className="font-mono text-xl sm:text-2xl text-[#FF4343] font-bold tabular-nums leading-tight truncate">${s.total_exec_payout.toLocaleString()}</p>
          </div>
          <div className="bg-[#111827] border border-white/10 p-3 sm:p-4 min-w-0">
            <p className="text-[10px] text-[#6B7280] uppercase">Total Cost</p>
            <p className="font-mono text-xl sm:text-2xl text-[#FF4343] font-bold tabular-nums leading-tight truncate">${s.total_brand_cost.toLocaleString()}</p>
            <p className="text-[10px] text-[#6B7280] truncate">incl. {(s.platform_fee_rate * 100).toFixed(0)}% fee: ${s.total_platform_fee.toLocaleString()}</p>
          </div>
        </div>

        {/* Amplify upsell — only shows once the campaign has real impressions
            worth boosting. Pre-fills the Services picker with a suggested
            channel + spend so the brand jumps straight into checkout. */}
        {s.total_impressions > 0 && (() => {
          const currentImpressions = s.total_impressions;
          const platforms = (tracking.campaign?.platforms || []).map(p => p.toLowerCase());
          const primaryPlatform =
            platforms.find(p => p.includes('linkedin')) ? 'linkedin' :
            platforms.find(p => p.includes('x') || p.includes('twitter')) ? 'x' :
            platforms.find(p => p.includes('youtube')) ? 'youtube' :
            platforms.find(p => p.includes('reddit')) ? 'reddit' :
            platforms.find(p => p.includes('newsletter') || p.includes('beehiiv')) ? 'beehiiv' :
            'linkedin';
          // Suggest 3x reach at a rough $30 CPM (adjust per channel)
          const cpmByChannel = { linkedin: 30, x: 12, meta: 15, google: 25, youtube: 18, reddit: 10, beehiiv: 40 };
          const cpm = cpmByChannel[primaryPlatform] || 20;
          const targetLift = 3;
          const additionalImpressions = currentImpressions * (targetLift - 1);
          const suggestedSpend = Math.max(
            2000,
            Math.round((additionalImpressions * cpm) / 1000 / 500) * 500
          );
          // 15% variable + $500 flat management fee (Freddy Media pricing)
          const totalWithFee = suggestedSpend * 1.15 + 500;
          return (
            <div
              className="bg-gradient-to-br from-[#FF4343]/15 via-[#FF4343]/5 to-[#111827] border border-[#FF4343]/40 p-5"
              data-testid="amplify-upsell"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Zap className="w-4 h-4 text-[#FF4343]" />
                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF4343]">Amplify this campaign</p>
                  </div>
                  <h4 className="text-base sm:text-lg font-semibold text-white mb-1.5">
                    {(currentImpressions / 1000).toFixed(0)}K organic impressions →{' '}
                    <span className="text-[#FF4343]">{((currentImpressions * targetLift) / 1000).toFixed(0)}K</span> with paid ads
                  </h4>
                  <p className="text-xs text-[#9CA3AF] leading-relaxed">
                    At a ~${cpm} CPM on {primaryPlatform === 'x' ? 'X' : primaryPlatform.charAt(0).toUpperCase() + primaryPlatform.slice(1)}, spending{' '}
                    <span className="font-mono text-white">${suggestedSpend.toLocaleString()}</span> would add
                    ~{(additionalImpressions / 1000).toFixed(0)}K impressions to this exec's post
                    (${totalWithFee.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})} total — includes $500 base + 15% mgmt fee).
                  </p>
                </div>
                <button
                  onClick={() => {
                    // Deep-link into the Services tab with the picker pre-filled
                    const params = new URLSearchParams({
                      tab: 'services',
                      channel: primaryPlatform,
                      spend: String(suggestedSpend),
                      linked_campaign_id: tracking.campaign?.id || '',
                    });
                    navigate(`/portal?${params.toString()}`);
                  }}
                  data-testid="amplify-upsell-cta"
                  className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2.5 text-xs font-semibold hover:bg-[#E03030] transition-all flex-shrink-0"
                >
                  Boost this campaign →
                </button>
              </div>
            </div>
          );
        })()}

        {/* Participants */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Executives ({tracking.participants.length})</h3>
          {tracking.participants.map(p => (
            <div key={p.id} className="bg-[#111827] border border-white/10 p-4" data-testid={`tracking-exec-${p.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  {p.exec_headshot && <img src={p.exec_headshot} alt="" className="w-10 h-10 object-cover" />}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white">{p.executive_name}</span>
                      {p.good_fit && <span className="text-[10px] px-1.5 py-0.5 bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20">Good Fit</span>}
                      {p.offer_type === 'bespoke' && <span className="text-[10px] px-1.5 py-0.5 bg-[#FF4343]/10 text-[#FF4343]">Bespoke</span>}
                    </div>
                    <p className="text-[10px] text-[#6B7280]">{p.platform} &middot; Status: {p.status} &middot; Draft: {p.draft_status || 'none'}</p>
                  </div>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button onClick={() => markGoodFit(p.id, !p.good_fit)} data-testid={`good-fit-${p.id}`}
                    className={`text-[10px] px-2 py-1 border transition-colors ${p.good_fit ? 'border-[#10B981] text-[#10B981]' : 'border-white/20 text-[#6B7280] hover:text-white'}`}>
                    {p.good_fit ? '★ Good Fit' : '☆ Mark Fit'}
                  </button>
                  <button onClick={() => inviteToNext(p.id)} data-testid={`invite-next-${p.id}`}
                    className="text-[10px] px-2 py-1 border border-[#FF4343]/50 text-[#FF4343] hover:bg-[#FF4343]/10 transition-colors">
                    Invite Next
                  </button>
                </div>
              </div>

              {/* Post metrics */}
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div><span className="text-[#6B7280]">Impressions</span><br /><span className="font-mono text-white">{p.impressions ? Number(p.impressions).toLocaleString() : '—'}</span></div>
                <div><span className="text-[#6B7280]">Engagement</span><br /><span className="font-mono text-[#10B981]">{p.engagement_rate ? `${p.engagement_rate}%` : '—'}</span></div>
                <div><span className="text-[#6B7280]">Payout</span><br /><span className="font-mono text-white">{p.payout_amount ? `$${Number(p.payout_amount).toLocaleString()}` : '—'}</span></div>
                <div><span className="text-[#6B7280]">Payment</span><br /><span className={`font-mono ${p.payout_status === 'paid' ? 'text-[#10B981]' : 'text-yellow-400'}`}>{p.payout_status}</span></div>
              </div>

              {/* Scheduled posts */}
              {p.scheduled_posts?.length > 0 && (
                <div className="mt-3 border-t border-white/5 pt-3">
                  <p className="text-[10px] text-[#6B7280] uppercase mb-2">Scheduled Posts ({p.scheduled_posts.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {p.scheduled_posts.map((sp, i) => (
                      <div key={i} className={`text-[10px] px-2 py-1 border ${sp.status === 'confirmed' ? 'border-[#10B981]/30 text-[#10B981]' : sp.status === 'proposed' ? 'border-yellow-500/30 text-yellow-400' : 'border-white/10 text-[#6B7280]'}`}>
                        {new Date(sp.date).toLocaleDateString()} — {sp.status}
                      </div>
                    ))}
                  </div>
                  {p.schedule_status === 'proposed' && (
                    <button onClick={() => confirmSchedule(p.id)} data-testid={`confirm-schedule-${p.id}`}
                      className="mt-2 text-[10px] px-3 py-1 border-2 border-[#10B981] bg-[#10B981] text-[#090E33] font-semibold hover:bg-[#059669] transition-all">
                      Confirm Schedule
                    </button>
                  )}
                </div>
              )}

              {/* Draft preview */}
              {p.draft_text && (
                <div className="mt-3 border-t border-white/5 pt-3">
                  <p className="text-[10px] text-[#6B7280] uppercase mb-1">Draft</p>
                  <p className="text-xs text-[#9CA3AF] line-clamp-2">{p.draft_text}</p>
                </div>
              )}

              {p.post_url && (
                <a href={p.post_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#FF4343] hover:underline mt-2">
                  <ExternalLink className="w-3 h-3" /> View Post
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Invite by Email Modal */}
        {showInvite && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowInvite(false)} />
            <div className="relative glass max-w-md w-full p-6" data-testid="invite-modal">
              <h3 className="font-display text-lg font-bold mb-4">Invite Executive <span className="text-[#FF4343]">by Email</span></h3>
              <div className="space-y-3">
                <input value={inviteForm.name} onChange={e => setInviteForm({...inviteForm, name: e.target.value})} placeholder="Full Name" data-testid="invite-name"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
                <input value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} placeholder="Email *" required data-testid="invite-email"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
                <div className="grid grid-cols-2 gap-3">
                  <input value={inviteForm.title} onChange={e => setInviteForm({...inviteForm, title: e.target.value})} placeholder="Job Title"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
                  <input value={inviteForm.company} onChange={e => setInviteForm({...inviteForm, company: e.target.value})} placeholder="Company"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
                </div>
                <input value={inviteForm.linkedin_url} onChange={e => setInviteForm({...inviteForm, linkedin_url: e.target.value})} placeholder="LinkedIn URL (optional)"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
                <textarea value={inviteForm.message} onChange={e => setInviteForm({...inviteForm, message: e.target.value})} placeholder="Personal message..." rows={2} data-testid="invite-message"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none" />
                <div className="flex gap-2 pt-2">
                  <button onClick={handleInviteByEmail} disabled={inviting || !inviteForm.email.trim()} data-testid="send-invite-btn"
                    className="flex-1 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-2 text-xs font-semibold hover:bg-[#E03030] disabled:opacity-50 transition-all">
                    {inviting ? 'Sending...' : 'Send Invitation'}
                  </button>
                  <button onClick={() => setShowInvite(false)} className="border border-white/20 text-[#9CA3AF] px-4 py-2 text-xs hover:text-white transition-all">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upload Contact List Modal */}
        {showUpload && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowUpload(false)} />
            <div className="relative glass max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto" data-testid="upload-modal">
              <h3 className="font-display text-lg font-bold mb-1">Upload <span className="text-[#FF4343]">Contact List</span></h3>
              <p className="text-xs text-[#6B7280] mb-4">Paste contacts in CSV format. One per line: Name, Email, Title, Company, LinkedIn URL</p>
              {uploadResult ? (
                <div className="text-center py-6" data-testid="upload-result">
                  <CheckCircle className="w-10 h-10 text-[#10B981] mx-auto mb-3" />
                  <p className="text-white font-semibold">{uploadResult.invited} invitations sent</p>
                  <p className="text-xs text-[#6B7280] mt-1">{uploadResult.skipped} skipped (already invited or invalid)</p>
                  <button onClick={() => setShowUpload(false)} className="mt-4 border border-white/20 text-white px-6 py-2 text-xs hover:bg-white/5 transition-all">Close</button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-white/5 border border-white/10 p-3">
                    <p className="text-[10px] text-[#6B7280] font-mono mb-2">Format: Name, Email, Title, Company, LinkedIn URL</p>
                    <p className="text-[10px] text-[#9CA3AF] font-mono">John Smith, john@dealer.com, Dealer Principal, Smith Auto, https://linkedin.com/in/johnsmith</p>
                  </div>
                  <textarea value={csvText} onChange={e => setCsvText(e.target.value)} rows={8} placeholder="Paste your contacts here..." data-testid="csv-input"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-xs font-mono placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none" />
                  <textarea value={uploadMessage} onChange={e => setUploadMessage(e.target.value)} rows={2} placeholder="Personal message included in all invitations (optional)" data-testid="upload-message"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none" />
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="private-list-toggle">
                    <input type="checkbox" checked={uploadPrivate} onChange={e => setUploadPrivate(e.target.checked)} className="w-3.5 h-3.5 accent-[#FF4343]" />
                    <span className="text-xs text-[#9CA3AF]">Keep this list private (contacts won't be searchable by other brands until they accept)</span>
                  </label>
                  <div className="flex gap-2">
                    <button onClick={handleUploadList} disabled={uploading || !csvText.trim()} data-testid="submit-upload-btn"
                      className="flex-1 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-2 text-xs font-semibold hover:bg-[#E03030] disabled:opacity-50 transition-all">
                      {uploading ? 'Uploading & Sending...' : `Upload & Send Invitations`}
                    </button>
                    <button onClick={() => setShowUpload(false)} className="border border-white/20 text-[#9CA3AF] px-4 py-2 text-xs hover:text-white transition-all">Cancel</button>
                  </div>
                  <p className="text-[9px] text-[#6B7280]">No Apollo charges — invitations are free when using your own contacts. Max 500 per upload.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Campaign List View
  return (
    <div data-testid="portal-campaigns">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">Campaigns</h3>
        <button onClick={() => setShowCreate(true)} data-testid="create-campaign-btn"
          className="flex items-center gap-1.5 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-4 py-2 text-xs font-semibold hover:bg-[#E03030] transition-all">
          + New Campaign
        </button>
      </div>
      {campaigns.length === 0 ? (
        <div className="text-center py-12 bg-[#111827] border border-white/10">
          <Briefcase className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
          <p className="text-[#9CA3AF] text-sm mb-4">No campaigns yet. Create your first campaign to start reaching executives.</p>
          <button onClick={() => setShowCreate(true)} data-testid="create-first-campaign-btn"
            className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-6 py-2.5 text-sm font-semibold hover:bg-[#E03030] transition-all">
            Create Your First Campaign
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => (
            <div key={c.id} className="bg-[#111827] border border-white/10 p-5 hover:border-white/20 transition-colors cursor-pointer" onClick={() => openTracking(c)} data-testid={`campaign-card-${c.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm text-white font-medium">{c.title}</h4>
                  <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 ${c.visibility === 'private' ? 'bg-white/5 text-[#6B7280]' : 'bg-[#10B981]/10 text-[#10B981]'}`}>
                    {c.visibility === 'private' ? 'Private' : 'Public'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-mono uppercase ${c.status === 'active' ? 'text-[#10B981]' : 'text-[#9CA3AF]'}`}>{c.status}</span>
                  <button onClick={(e) => { e.stopPropagation(); setBudgetModal(c); setBudgetAmount('500'); }} data-testid={`add-budget-${c.id}`}
                    className="text-[10px] px-3 py-1 border border-[#FF4343] text-[#FF4343] hover:bg-[#FF4343] hover:text-[#090E33] transition-colors flex items-center gap-1">
                    <DollarSign className="w-3 h-3" /> Fund
                  </button>
                  <ChevronRight className="w-4 h-4 text-[#6B7280]" />
                </div>
              </div>
              <div className="flex gap-4 text-xs text-[#6B7280]">
                <span>Posts: {c.posts_submitted || 0}/{c.spots_total || 0}</span>
                <span>Impressions: {c.total_impressions ? `${(c.total_impressions/1000).toFixed(0)}K` : '0'}</span>
                <span>{c.service_type === 'managed' ? 'Managed' : 'Self-Service'}</span>
              </div>
              {c.spots_total > 0 && (
                <div className="mt-2 w-full h-1.5 bg-white/5"><div className="h-full bg-[#FF4343]" style={{ width: `${((c.posts_submitted || 0) / c.spots_total) * 100}%` }} /></div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Budget Modal */}
      {budgetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setBudgetModal(null)} />
          <div className="relative glass max-w-sm w-full p-6" data-testid="add-budget-modal">
            <h3 className="text-sm font-semibold mb-1">Add Campaign Budget</h3>
            <p className="text-[10px] text-[#6B7280] mb-4">{budgetModal.title}</p>
            <label className="text-[10px] font-mono uppercase text-[#6B7280] mb-1.5 block">Amount (USD)</label>
            <div className="flex gap-2 mb-4">
              {['250', '500', '1000', '2500'].map(a => (
                <button key={a} onClick={() => setBudgetAmount(a)}
                  className={`text-xs px-3 py-2 border font-mono ${budgetAmount === a ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#6B7280]'}`}>
                  ${parseInt(a).toLocaleString()}
                </button>
              ))}
            </div>
            <input type="number" min="100" value={budgetAmount} onChange={e => setBudgetAmount(e.target.value)} data-testid="budget-amount-input"
              className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm font-mono focus:outline-none focus:border-[#FF4343] mb-4" />
            <div className="flex gap-2">
              <button onClick={handleAddBudget} disabled={budgetLoading || !budgetAmount || parseFloat(budgetAmount) < 100} data-testid="confirm-budget-btn"
                className="flex-1 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-2.5 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-50 transition-all">
                {budgetLoading ? 'Processing...' : `Pay $${parseFloat(budgetAmount || 0).toLocaleString()}`}
              </button>
              <button onClick={() => setBudgetModal(null)} className="border border-white/20 text-[#9CA3AF] px-4 py-2 text-sm hover:text-white transition-colors">
                Cancel
              </button>
            </div>
            <p className="text-[9px] text-[#6B7280] mt-3 text-center">Secure payment via Stripe. Minimum $100.</p>
          </div>
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="create-campaign-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreate(false)} />
          <div className="relative glass max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-display text-xl font-bold mb-1">Create <span className="text-[#FF4343]">Campaign</span></h3>
            <p className="text-xs text-[#6B7280] mb-6">Set up your campaign brief. Executives will see these details when reviewing your offer.</p>

            <div className="space-y-4">
              {/* Offering type picker */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] text-[#6B7280] uppercase block">What do you want from your executives? *</label>
                  <button type="button" onClick={() => setShowRecommender(true)} data-testid="open-recommender"
                    className="text-[10px] uppercase font-mono text-[#FF4343] hover:underline flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> What should I run?
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {offeringTypes.map(t => {
                    const selected = newCampaign.offering_type === t.key;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setNewCampaign({ ...newCampaign, offering_type: t.key })}
                        data-testid={`offering-type-${t.key}`}
                        className={`text-left p-3 border-2 transition-all ${selected ? 'border-[#FF4343] bg-[#FF4343]/10' : 'border-white/10 bg-white/5 hover:border-white/30'}`}
                      >
                        <div className="text-[9px] font-mono uppercase tracking-wider text-[#FF4343] mb-1">{tierMeta[t.tier]?.label || t.tier}</div>
                        <div className="text-sm font-semibold text-white mb-1">{t.label}</div>
                        <div className="text-[10px] text-[#9CA3AF] mb-2 leading-snug">{t.tagline}</div>
                        <div className="text-[10px] text-[#6B7280] font-mono">${t.suggested_range_usd[0].toLocaleString()}–${t.suggested_range_usd[1].toLocaleString()}</div>
                      </button>
                    );
                  })}
                </div>
                {newCampaign.offering_type && offeringTypes.length > 0 && (() => {
                  const ot = offeringTypes.find(t => t.key === newCampaign.offering_type);
                  if (!ot) return null;
                  return (
                    <p className="text-[10px] text-[#6B7280] mt-2 italic">
                      <span className="text-[#9CA3AF]">Suggested range:</span> ${ot.suggested_range_usd[0].toLocaleString()}–${ot.suggested_range_usd[1].toLocaleString()}. {ot.pricing_note} <span className="text-[#FF4343]">Every industry is different — adjust to fit your market.</span>
                    </p>
                  );
                })()}
              </div>

              <div>
                <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Campaign Title *</label>
                <input value={newCampaign.title} onChange={e => setNewCampaign({...newCampaign, title: e.target.value})} placeholder="e.g., Automotive CRM Endorsers Wanted" data-testid="new-campaign-title"
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
              </div>
              <div>
                <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Description</label>
                <textarea value={newCampaign.description} onChange={e => setNewCampaign({...newCampaign, description: e.target.value})} rows={3} placeholder="Describe what you're looking for..." data-testid="new-campaign-desc"
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none" />
              </div>

              {/* Survey builder — only shown when survey type is selected */}
              {newCampaign.offering_type === 'survey' && (
                <div className="border border-[#FF4343]/30 bg-[#FF4343]/5 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-white">Survey Setup</h4>
                    <div className="flex gap-1 bg-white/5 border border-white/10 p-1">
                      <button type="button" onClick={() => setNewCampaign({ ...newCampaign, survey_external_url: '', survey_questions: newCampaign.survey_questions.length > 0 ? newCampaign.survey_questions : [{ type: 'rating', prompt: '', required: true, options: [] }] })}
                        className={`px-3 py-1 text-[10px] font-mono uppercase ${!newCampaign.survey_external_url ? 'bg-[#FF4343] text-[#090E33]' : 'text-[#9CA3AF]'}`}>Native</button>
                      <button type="button" onClick={() => setNewCampaign({ ...newCampaign, survey_questions: [], survey_external_url: newCampaign.survey_external_url || 'https://' })}
                        className={`px-3 py-1 text-[10px] font-mono uppercase ${newCampaign.survey_external_url ? 'bg-[#FF4343] text-[#090E33]' : 'text-[#9CA3AF]'}`}>External URL</button>
                    </div>
                  </div>

                  {newCampaign.survey_external_url ? (
                    <div>
                      <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Survey URL (Typeform / Google Forms / etc.)</label>
                      <input type="url" value={newCampaign.survey_external_url} onChange={e => setNewCampaign({ ...newCampaign, survey_external_url: e.target.value })} data-testid="survey-external-url"
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" placeholder="https://forms.example.com/your-survey" />
                      <p className="text-[10px] text-[#6B7280] mt-1">Execs click through to your external survey. We track completion via their participant record.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {newCampaign.survey_questions.map((q, qi) => (
                        <div key={qi} className="bg-white/5 border border-white/10 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-mono uppercase text-[#6B7280]">Question {qi + 1}</span>
                            <button type="button" onClick={() => setNewCampaign({ ...newCampaign, survey_questions: newCampaign.survey_questions.filter((_, i) => i !== qi) })} className="text-[#FF4343] text-xs hover:underline">Remove</button>
                          </div>
                          <select value={q.type} onChange={e => {
                            const updated = [...newCampaign.survey_questions];
                            updated[qi] = { ...updated[qi], type: e.target.value, options: e.target.value === 'multiple_choice' ? (updated[qi].options || ['', '']) : [] };
                            setNewCampaign({ ...newCampaign, survey_questions: updated });
                          }} className="w-full mb-2 px-3 py-2 bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
                            <option value="rating">Rating (1–5)</option>
                            <option value="multiple_choice">Multiple Choice</option>
                            <option value="short_text">Short Text</option>
                            <option value="long_text">Long Text</option>
                          </select>
                          <input value={q.prompt} onChange={e => {
                            const updated = [...newCampaign.survey_questions];
                            updated[qi] = { ...updated[qi], prompt: e.target.value };
                            setNewCampaign({ ...newCampaign, survey_questions: updated });
                          }} placeholder="Question text..." data-testid={`survey-q-${qi}-prompt`}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                          {q.type === 'multiple_choice' && (
                            <div className="mt-2 space-y-1">
                              {(q.options || []).map((opt, oi) => (
                                <div key={oi} className="flex gap-2">
                                  <input value={opt} onChange={e => {
                                    const updated = [...newCampaign.survey_questions];
                                    updated[qi].options[oi] = e.target.value;
                                    setNewCampaign({ ...newCampaign, survey_questions: updated });
                                  }} placeholder={`Option ${oi + 1}`}
                                    className="flex-1 px-2 py-1 bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-[#FF4343]" />
                                  <button type="button" onClick={() => {
                                    const updated = [...newCampaign.survey_questions];
                                    updated[qi].options = updated[qi].options.filter((_, i) => i !== oi);
                                    setNewCampaign({ ...newCampaign, survey_questions: updated });
                                  }} className="text-[#FF4343] text-xs">×</button>
                                </div>
                              ))}
                              <button type="button" onClick={() => {
                                const updated = [...newCampaign.survey_questions];
                                updated[qi].options = [...(updated[qi].options || []), ''];
                                setNewCampaign({ ...newCampaign, survey_questions: updated });
                              }} className="text-[10px] text-[#FF4343] hover:underline">+ Add Option</button>
                            </div>
                          )}
                          <label className="flex items-center gap-2 mt-2 text-[10px] text-[#9CA3AF]">
                            <input type="checkbox" checked={q.required !== false} onChange={e => {
                              const updated = [...newCampaign.survey_questions];
                              updated[qi] = { ...updated[qi], required: e.target.checked };
                              setNewCampaign({ ...newCampaign, survey_questions: updated });
                            }} /> Required
                          </label>
                        </div>
                      ))}
                      <button type="button" onClick={() => setNewCampaign({ ...newCampaign, survey_questions: [...newCampaign.survey_questions, { type: 'rating', prompt: '', required: true, options: [] }] })}
                        data-testid="survey-add-question"
                        className="w-full border border-dashed border-white/20 text-[#9CA3AF] hover:text-white hover:border-white/40 px-3 py-2 text-xs uppercase font-mono">
                        + Add Question
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Session duration — feedback_session + advisory_call + sales_pitch */}
              {(newCampaign.offering_type === 'feedback_session' || newCampaign.offering_type === 'advisory_call' || newCampaign.offering_type === 'sales_pitch') && (
                <div className="border border-[#FF4343]/30 bg-[#FF4343]/5 p-4">
                  <h4 className="text-sm font-semibold text-white mb-2">Session Setup</h4>
                  <p className="text-[10px] text-[#6B7280] mb-3">
                    {newCampaign.offering_type === 'sales_pitch'
                      ? "💡 Tip: Use the dedicated 'Find Executives Open to Pitch' page to book a specific executive at their own rate."
                      : 'Executives will propose 3-5 time slots from their availability. You confirm one and paste the meeting URL.'}
                  </p>
                  <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Duration (minutes)</label>
                  <select value={newCampaign.session_duration_min} onChange={e => setNewCampaign({ ...newCampaign, session_duration_min: parseInt(e.target.value) })}
                    data-testid="session-duration"
                    className="w-full px-3 py-2 bg-[#111827] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
                    {[15, 30, 45, 60, 90].map(m => <option key={m} value={m}>{m} min</option>)}
                  </select>
                </div>
              )}

              {/* Consulting project — scope + milestones */}
              {newCampaign.offering_type === 'consulting_project' && (
                <div className="border border-[#FF4343]/30 bg-[#FF4343]/5 p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-white">Project Setup</h4>
                  <p className="text-[10px] text-[#6B7280]">Define the scope of work and break it into milestones. Each milestone payout is released automatically when you approve the deliverable.</p>
                  <div>
                    <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Scope of Work *</label>
                    <textarea value={newCampaign.project_scope} onChange={e => setNewCampaign({ ...newCampaign, project_scope: e.target.value })} rows={3}
                      data-testid="project-scope"
                      placeholder="What's the desired outcome? Constraints, timeline, deliverable format..."
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Milestones *</label>
                    {newCampaign.project_milestones.map((m, mi) => (
                      <div key={mi} className="bg-white/5 border border-white/10 p-3 mb-2">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[10px] font-mono uppercase text-[#6B7280]">Milestone {mi + 1}</span>
                          <button type="button" onClick={() => setNewCampaign({ ...newCampaign, project_milestones: newCampaign.project_milestones.filter((_, i) => i !== mi) })} className="text-[#FF4343] text-xs hover:underline">Remove</button>
                        </div>
                        <input value={m.title} onChange={e => { const u = [...newCampaign.project_milestones]; u[mi] = { ...u[mi], title: e.target.value }; setNewCampaign({ ...newCampaign, project_milestones: u }); }}
                          placeholder="Milestone title (e.g., 'Discovery + Audit')" data-testid={`milestone-${mi}-title`}
                          className="w-full mb-2 px-3 py-2 bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                        <textarea value={m.description || ''} onChange={e => { const u = [...newCampaign.project_milestones]; u[mi] = { ...u[mi], description: e.target.value }; setNewCampaign({ ...newCampaign, project_milestones: u }); }}
                          rows={2} placeholder="Deliverable description..."
                          className="w-full mb-2 px-3 py-2 bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-[#FF4343] resize-none" />
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-[#6B7280] uppercase">Payout ($)</label>
                            <input type="number" value={m.amount} onChange={e => { const u = [...newCampaign.project_milestones]; u[mi] = { ...u[mi], amount: e.target.value }; setNewCampaign({ ...newCampaign, project_milestones: u }); }}
                              placeholder="2500" data-testid={`milestone-${mi}-amount`}
                              className="w-full px-2 py-1.5 bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                          </div>
                          <div>
                            <label className="text-[10px] text-[#6B7280] uppercase">Due Date</label>
                            <input type="date" value={m.due_date || ''} onChange={e => { const u = [...newCampaign.project_milestones]; u[mi] = { ...u[mi], due_date: e.target.value }; setNewCampaign({ ...newCampaign, project_milestones: u }); }}
                              className="w-full px-2 py-1.5 bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button type="button" onClick={() => setNewCampaign({ ...newCampaign, project_milestones: [...newCampaign.project_milestones, { title: '', description: '', amount: '', due_date: '' }] })}
                      data-testid="add-milestone"
                      className="w-full border border-dashed border-white/20 text-[#9CA3AF] hover:text-white hover:border-white/40 px-3 py-2 text-xs uppercase font-mono">
                      + Add Milestone
                    </button>
                    {newCampaign.project_milestones.length > 0 && (
                      <p className="text-[10px] text-[#FF4343] mt-2 text-right font-mono">
                        Total project value: ${newCampaign.project_milestones.reduce((s, m) => s + parseFloat(m.amount || 0), 0).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* AI Training Q&A — question set builder */}
              {newCampaign.offering_type === 'ai_training' && (
                <div className="border border-[#FF4343]/30 bg-[#FF4343]/5 p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-white">AI Training Question Set</h4>
                  <p className="text-[10px] text-[#6B7280]">List the questions you want the executive to answer. After submission, you download a JSONL dataset ready to fine-tune Claude or OpenAI on their domain expertise.</p>
                  <div>
                    <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Optional: pay per question ($)</label>
                    <input type="number" value={newCampaign.ai_training_pay_per_question} onChange={e => setNewCampaign({ ...newCampaign, ai_training_pay_per_question: e.target.value })}
                      data-testid="ai-training-pay-per-q" placeholder="0 = use flat fee"
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                  </div>
                  {newCampaign.ai_training_questions.map((q, qi) => (
                    <div key={qi} className="bg-white/5 border border-white/10 p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-mono uppercase text-[#6B7280]">Question {qi + 1}</span>
                        <button type="button" onClick={() => setNewCampaign({ ...newCampaign, ai_training_questions: newCampaign.ai_training_questions.filter((_, i) => i !== qi) })} className="text-[#FF4343] text-xs hover:underline">Remove</button>
                      </div>
                      <textarea value={q.prompt} onChange={e => { const u = [...newCampaign.ai_training_questions]; u[qi] = { ...u[qi], prompt: e.target.value }; setNewCampaign({ ...newCampaign, ai_training_questions: u }); }}
                        rows={2} placeholder="Question / prompt (e.g., 'How do you decide between hiring an SDR vs. an AE when scaling a Series A team?')"
                        data-testid={`ai-training-q-${qi}-prompt`}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none" />
                      <input value={q.context || ''} onChange={e => { const u = [...newCampaign.ai_training_questions]; u[qi] = { ...u[qi], context: e.target.value }; setNewCampaign({ ...newCampaign, ai_training_questions: u }); }}
                        placeholder="Optional context (e.g., 'Answer assuming a B2B SaaS startup with $2M ARR')"
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
                      <div className="grid grid-cols-2 gap-2">
                        <input value={q.category || ''} onChange={e => { const u = [...newCampaign.ai_training_questions]; u[qi] = { ...u[qi], category: e.target.value }; setNewCampaign({ ...newCampaign, ai_training_questions: u }); }}
                          placeholder="Category (Hiring, Pricing…)"
                          className="px-3 py-2 bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-[#FF4343]" />
                        <div className="flex items-center bg-white/5 border border-white/10 px-2">
                          <span className="text-[10px] text-[#6B7280]">Min words:</span>
                          <input type="number" value={q.min_answer_words || 30} onChange={e => { const u = [...newCampaign.ai_training_questions]; u[qi] = { ...u[qi], min_answer_words: parseInt(e.target.value || 30) }; setNewCampaign({ ...newCampaign, ai_training_questions: u }); }}
                            className="flex-1 px-1 py-1.5 bg-transparent text-white text-xs focus:outline-none" />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button type="button" onClick={() => setNewCampaign({ ...newCampaign, ai_training_questions: [...newCampaign.ai_training_questions, { prompt: '', context: '', category: '', min_answer_words: 30 }] })}
                    data-testid="add-ai-training-question"
                    className="w-full border border-dashed border-white/20 text-[#9CA3AF] hover:text-white hover:border-white/40 px-3 py-2 text-xs uppercase font-mono">
                    + Add Question
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Industry</label>
                  <select value={newCampaign.industry} onChange={e => setNewCampaign({...newCampaign, industry: e.target.value})} data-testid="new-campaign-industry"
                    className="w-full px-3 py-2.5 bg-[#111827] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
                    <option value="">Select Industry</option>
                    {['Automotive','Real Estate','Medical','Tech','Finance','Legal','Retail','Manufacturing','Food & Hospitality','Marketing/Media','Energy','Logistics','Agriculture','Other'].map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Visibility</label>
                  <select value={newCampaign.visibility} onChange={e => setNewCampaign({...newCampaign, visibility: e.target.value})} data-testid="new-campaign-visibility"
                    className="w-full px-3 py-2.5 bg-[#111827] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
                    <option value="public">Public — Listed on campaign board</option>
                    <option value="private">Private — Invite only</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Platforms</label>
                <div className="flex flex-wrap gap-2">
                  {['LinkedIn','X','Instagram','YouTube','Newsletter','Podcast','TikTok','Facebook','In-Person Event','Trade Publication'].map(p => (
                    <button key={p} onClick={() => setNewCampaign(nc => ({...nc, platforms: nc.platforms.includes(p) ? nc.platforms.filter(x => x !== p) : [...nc.platforms, p]}))}
                      className={`text-xs px-3 py-1.5 border transition-colors ${newCampaign.platforms.includes(p) ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#9CA3AF]'}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <label className="text-[10px] text-[#6B7280] uppercase mb-2 block">Compensation</label>
                <div className="flex gap-2 mb-4">
                  {[{k:'flat',l:'Flat Fee'},{k:'cpm',l:'CPM'},{k:'hybrid',l:'Hybrid'}].map(t => (
                    <button key={t.k} onClick={() => setNewCampaign({...newCampaign, compensation_type: t.k})}
                      className={`text-xs px-4 py-2 border transition-colors ${newCampaign.compensation_type === t.k ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#9CA3AF]'}`}>
                      {t.l}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {(newCampaign.compensation_type === 'flat' || newCampaign.compensation_type === 'hybrid') && (
                    <div>
                      <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Flat Fee ($)</label>
                      <input type="number" value={newCampaign.flat_fee} onChange={e => setNewCampaign({...newCampaign, flat_fee: e.target.value})} placeholder="1500" data-testid="new-campaign-flat-fee"
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                    </div>
                  )}
                  {(newCampaign.compensation_type === 'cpm' || newCampaign.compensation_type === 'hybrid') && (
                    <div>
                      <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">CPM Rate ($)</label>
                      <input type="number" value={newCampaign.cpm_rate} onChange={e => setNewCampaign({...newCampaign, cpm_rate: e.target.value})} placeholder="25" data-testid="new-campaign-cpm"
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                    </div>
                  )}
                  <div>
                    <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Floor Guarantee ($)</label>
                    <input type="number" value={newCampaign.floor_guarantee} onChange={e => setNewCampaign({...newCampaign, floor_guarantee: e.target.value})} placeholder="500"
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Spots Available</label>
                    <input type="number" value={newCampaign.spots_total} onChange={e => setNewCampaign({...newCampaign, spots_total: parseInt(e.target.value) || 10})} data-testid="new-campaign-spots"
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Exec Tier Required</label>
                  <select value={newCampaign.exec_tier_required} onChange={e => setNewCampaign({...newCampaign, exec_tier_required: e.target.value})}
                    className="w-full px-3 py-2 bg-[#111827] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
                    {['Open','Director+','C-Suite / Owner','Elite Only'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Deadline</label>
                  <input type="date" value={newCampaign.deadline?.split('T')[0] || ''} onChange={e => setNewCampaign({...newCampaign, deadline: e.target.value ? e.target.value + 'T00:00:00Z' : ''})} data-testid="new-campaign-deadline"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                </div>
              </div>

              <div>
                <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Talking Points</label>
                {newCampaign.talking_points.map((tp, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input value={tp} onChange={e => { const t = [...newCampaign.talking_points]; t[i] = e.target.value; setNewCampaign({...newCampaign, talking_points: t}); }} placeholder={`Talking point ${i+1}`}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
                    {i > 0 && <button onClick={() => setNewCampaign({...newCampaign, talking_points: newCampaign.talking_points.filter((_,j) => j !== i)})} className="text-xs text-red-400 px-2">×</button>}
                  </div>
                ))}
                <button onClick={() => setNewCampaign({...newCampaign, talking_points: [...newCampaign.talking_points, '']})} className="text-xs text-[#FF4343] hover:underline">+ Add Talking Point</button>
              </div>

              <div>
                <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Guardrails / Do's & Don'ts</label>
                {newCampaign.guardrails.map((g, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input value={g} onChange={e => { const gr = [...newCampaign.guardrails]; gr[i] = e.target.value; setNewCampaign({...newCampaign, guardrails: gr}); }} placeholder={`Guardrail ${i+1}`}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
                    {i > 0 && <button onClick={() => setNewCampaign({...newCampaign, guardrails: newCampaign.guardrails.filter((_,j) => j !== i)})} className="text-xs text-red-400 px-2">×</button>}
                  </div>
                ))}
                <button onClick={() => setNewCampaign({...newCampaign, guardrails: [...newCampaign.guardrails, '']})} className="text-xs text-[#FF4343] hover:underline">+ Add Guardrail</button>
              </div>

              <div>
                <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Example Post (Optional)</label>
                <textarea value={newCampaign.example_post} onChange={e => setNewCampaign({...newCampaign, example_post: e.target.value})} rows={2} placeholder="Show executives what a great post looks like..."
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none" />
              </div>

              <div className="flex gap-3 pt-4 border-t border-white/10">
                <button onClick={handleCreateCampaign} disabled={creating || !newCampaign.title.trim()} data-testid="submit-create-campaign"
                  className="flex-1 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-2.5 text-sm font-semibold hover:bg-[#E03030] transition-all disabled:opacity-50">
                  {creating ? 'Creating...' : 'Launch Campaign'}
                </button>
                <button onClick={() => setShowCreate(false)} className="border border-white/20 text-[#9CA3AF] px-6 py-2 text-sm hover:text-white transition-colors">Cancel</button>
              </div>
              <p className="text-[9px] text-[#6B7280] text-center">Self-service campaigns include a 15% platform fee on executive payouts.</p>
            </div>
          </div>
        </div>
      )}

      {/* Smart Offering Recommender Wizard */}
      {showRecommender && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !recoBusy && setShowRecommender(false)} />
          <div className="relative glass max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="recommender-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#FF4343]" /> Smart <span className="text-[#FF4343]">Recommender</span></h3>
              <button onClick={() => setShowRecommender(false)} className="text-[#9CA3AF] hover:text-white"><XCircle className="w-4 h-4" /></button>
            </div>
            {!recoResult ? (
              <>
                <p className="text-xs text-[#9CA3AF] mb-4">Tell us your goal in plain English. We'll suggest the best offering + budget range.</p>
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Your goal *</label>
                    <textarea value={recoForm.goal} onChange={e => setRecoForm({ ...recoForm, goal: e.target.value })} rows={3}
                      data-testid="reco-goal"
                      placeholder="e.g., I need social proof for our enterprise landing page and a few short videos for paid ads."
                      className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Funnel Stage</label>
                      <select value={recoForm.funnel_stage} onChange={e => setRecoForm({ ...recoForm, funnel_stage: e.target.value })} data-testid="reco-funnel"
                        className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
                        <option value="awareness">Awareness</option>
                        <option value="consideration">Consideration</option>
                        <option value="decision">Decision</option>
                        <option value="retention">Retention</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-[#6B7280] uppercase mb-1 block">Budget Band</label>
                      <select value={recoForm.budget_band} onChange={e => setRecoForm({ ...recoForm, budget_band: e.target.value })} data-testid="reco-budget"
                        className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
                        <option>&lt; $5K</option>
                        <option>$5-25K</option>
                        <option>$25K+</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={async () => {
                    if (!recoForm.goal.trim()) { toast.error('Tell us your goal first'); return; }
                    setRecoBusy(true);
                    try {
                      const { data } = await axios.post(`${API}/ai/offering-recommend`, recoForm, { headers: getHeaders() });
                      setRecoResult(data);
                    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                    finally { setRecoBusy(false); }
                  }} disabled={recoBusy || !recoForm.goal.trim()} data-testid="reco-submit"
                    className="flex-1 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold disabled:opacity-50">{recoBusy ? 'Thinking…' : 'Get Recommendation'}</button>
                  <button onClick={() => setShowRecommender(false)} disabled={recoBusy} className="border border-white/20 text-[#9CA3AF] px-5 py-2 text-sm">Cancel</button>
                </div>
              </>
            ) : (
              <div className="space-y-3" data-testid="reco-result">
                <div className="bg-[#FF4343]/10 border border-[#FF4343]/30 p-4">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-[#FF4343] mb-1">Recommended Offering</p>
                  <h4 className="text-lg font-semibold text-white">{(offeringTypes.find(o => o.key === recoResult.offering_type) || {}).label || recoResult.offering_type}</h4>
                  <p className="text-xs text-[#9CA3AF] mt-2 leading-relaxed">{recoResult.rationale}</p>
                  <p className="text-[10px] text-[#FF4343] mt-3 font-mono">Suggested budget: ${recoResult.budget_min?.toLocaleString()} – ${recoResult.budget_max?.toLocaleString()}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => {
                    setNewCampaign({ ...newCampaign, offering_type: recoResult.offering_type, flat_fee: String(recoResult.budget_min || '') });
                    setShowRecommender(false);
                    setRecoResult(null);
                    toast.success('Pre-filled — finish the campaign details below.');
                  }} data-testid="reco-apply"
                    className="flex-1 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold">Use This</button>
                  <button onClick={() => setRecoResult(null)} className="border border-white/20 text-[#9CA3AF] px-5 py-2 text-sm">Try Again</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

